import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  SelectChangeEvent
} from '@mui/material';
import { Login, AdminPanelSettings, School, Visibility, Analytics } from '@mui/icons-material';
import { useAuthContext } from '../../contexts/AuthContext';

interface RoleOption {
  value: string;
  label: string;
  icon: React.ReactNode;
  roles: string[];
  description: string;
}

const roleOptions: RoleOption[] = [
  {
    value: 'administrator',
    label: 'Administrator',
    icon: <AdminPanelSettings />,
    roles: ['administrator', 'user_access_administrator', 'analyst', 'viewer'],
    description: 'Full access to all features and data'
  },
  {
    value: 'analyst',
    label: 'Analyst',
    icon: <Analytics />,
    roles: ['analyst', 'viewer'],
    description: 'Can analyze data and create reports'
  },
  {
    value: 'viewer',
    label: 'Viewer',
    icon: <Visibility />,
    roles: ['viewer'],
    description: 'Read-only access to data'
  },
  {
    value: 'student',
    label: 'Student',
    icon: <School />,
    roles: ['student'],
    description: 'Only Itinerary tab - can only view/edit own data'
  }
];

const RoleSelector: React.FC = () => {
  const { user, isAuthenticated } = useAuthContext();
  const [selectedRole, setSelectedRole] = useState<string>('administrator');
  const [username, setUsername] = useState<string>('Student-01');

  // Hide Role Selector in production mode
  // Check if explicitly set to production mode (not demo)
  const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';
  const isProductionMode = process.env.REACT_APP_DEMO_MODE === 'false';
  
  // Hide Role Selector if in production mode OR if not in demo mode
  if (isProductionMode || (process.env.REACT_APP_DEMO_MODE !== undefined && !isDemoMode)) {
    return null;
  }

  const handleRoleChange = (event: SelectChangeEvent) => {
    const role = event.target.value;
    setSelectedRole(role);
    
    // Auto-set username based on role
    if (role === 'student') {
      setUsername('Student-01');
    } else if (role === 'analyst') {
      setUsername('analyst_user');
    } else if (role === 'viewer') {
      setUsername('viewer_user');
    } else {
      setUsername('admin_user');
    }
  };

  const handleLogin = () => {
    const roleOption = roleOptions.find(r => r.value === selectedRole);
    if (!roleOption) return;

    const testUser = {
      username: username,
      email: `${username}@localhost.dev`,
      name: username.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
      roles: roleOption.roles,
      groups: roleOption.roles.map(r => `GIS-${r.charAt(0).toUpperCase() + r.slice(1)}s`),
      is_authenticated: true
    };

    // Store in localStorage
    localStorage.setItem('access_token', 'local-test-token');
    localStorage.setItem('demo_user', JSON.stringify(testUser));
    
    // Refresh page to trigger auth check
    window.location.reload();
  };

  if (isAuthenticated && user) {
    return (
      <Box sx={{ mb: 3 }}>
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2">
            ✅ Logged in as: <strong>{user.name}</strong>
          </Typography>
          <Typography variant="caption" display="block">
            Username: {user.username} | Roles: {user.roles.join(', ')}
          </Typography>
        </Alert>
        <Button
          variant="outlined"
          size="small"
          fullWidth
          onClick={() => {
            localStorage.removeItem('access_token');
            localStorage.removeItem('demo_user');
            window.location.reload();
          }}
        >
          Logout & Change Role
        </Button>
      </Box>
    );
  }

  const currentRoleOption = roleOptions.find(r => r.value === selectedRole);

  return (
    <Box sx={{ mb: 3 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <AdminPanelSettings sx={{ fontSize: 32, color: 'primary.main', mr: 1 }} />
            <Typography variant="h6">
              Local Testing Mode
            </Typography>
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Choose a role to test RBAC functionality without Azure AD
          </Typography>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Role</InputLabel>
            <Select
              value={selectedRole}
              onChange={handleRoleChange}
              label="Select Role"
            >
              {roleOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ mr: 1, display: 'flex' }}>{option.icon}</Box>
                    {option.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {currentRoleOption && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                {currentRoleOption.label}
              </Typography>
              <Typography variant="caption">
                {currentRoleOption.description}
              </Typography>
            </Alert>
          )}

          <TextField
            fullWidth
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            sx={{ mb: 2 }}
            helperText={selectedRole === 'student' ? 'Use Student-01, Student-02, etc. to test student access' : 'Username for this test session'}
          />

          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={<Login />}
            onClick={handleLogin}
            sx={{ mb: 1 }}
          >
            Login as {currentRoleOption?.label}
          </Button>

          <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary', textAlign: 'center' }}>
            Development only - bypasses Azure AD authentication
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default RoleSelector;
